/* Shared presentational bits for the User Management / RBAC module.
   Kept dependency-light and theme-driven (uses the same CSS-module styles). */

import React, { useEffect } from "react";
import s from "./userManagement.module.css";
import { IoClose, IoWarningOutline, IoSearchOutline } from "react-icons/io5";

export const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

export const Avatar = ({ name, color, size = 40 }) => (
  <span
    className={s.avatar}
    style={{
      width: size,
      height: size,
      background: color || "var(--accent-base, #b87333)",
      fontSize: size * 0.38,
    }}
    aria-hidden="true"
  >
    {initials(name)}
  </span>
);

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  invited: "Invited",
};

export const StatusBadge = ({ status }) => (
  <span className={`${s.statusBadge} ${s[`status_${status}`] || ""}`}>
    <span className={s.statusDot} />
    {STATUS_LABELS[status] || status}
  </span>
);

export const RoleChip = ({ role, onRemove }) => (
  <span className={s.roleChip} style={{ "--chip": role?.color || "#888" }}>
    <span className={s.roleChipDot} />
    {role?.name || "Unknown"}
    {onRemove && (
      <button
        type="button"
        className={s.roleChipRemove}
        onClick={onRemove}
        aria-label={`Remove ${role?.name}`}
      >
        <IoClose />
      </button>
    )}
  </span>
);

export const Spinner = ({ label }) => (
  <div className={s.spinnerWrap}>
    <span className={s.spinner} />
    {label && <span className={s.spinnerLabel}>{label}</span>}
  </div>
);

export const EmptyState = ({ icon, title, message, action }) => (
  <div className={s.emptyState}>
    {icon && <div className={s.emptyIcon}>{icon}</div>}
    <div className={s.emptyTitle}>{title}</div>
    {message && <div className={s.emptyMessage}>{message}</div>}
    {action}
  </div>
);

export const SearchInput = ({ value, onChange, placeholder }) => (
  <div className={s.searchInput}>
    <IoSearchOutline className={s.searchInputIcon} />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder || "Search…"}
    />
  </div>
);

export const Pagination = ({ page, pageSize, total, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className={s.pagination}>
      <span className={s.paginationInfo}>
        {start}–{end} of {total}
      </span>
      <div className={s.paginationBtns}>
        <button
          type="button"
          className={s.pageBtn}
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Previous
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(
            (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1
          )
          .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
            acc.push(p);
            return acc;
          }, [])
          .map((p, i) =>
            p === "…" ? (
              <span key={`e${i}`} className={s.pageEllipsis}>
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`${s.pageBtn} ${p === page ? s.pageBtnActive : ""}`}
                onClick={() => onChange(p)}
              >
                {p}
              </button>
            )
          )}
        <button
          type="button"
          className={s.pageBtn}
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    className={`${s.toggle} ${checked ? s.toggleOn : ""}`}
    onClick={() => !disabled && onChange(!checked)}
  >
    <span className={s.toggleKnob} />
  </button>
);

export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onCancel?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className={s.dialogOverlay} onClick={onCancel}>
      <div className={s.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={`${s.dialogIcon} ${danger ? s.dialogIconDanger : ""}`}>
          <IoWarningOutline />
        </div>
        <div className={s.dialogTitle}>{title}</div>
        {message && <div className={s.dialogMessage}>{message}</div>}
        <div className={s.dialogActions}>
          <button
            type="button"
            className={s.btnGhost}
            onClick={onCancel}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? s.btnDanger : s.btnPrimary}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
